import WrapInputLabel from 'common/components/wrap-input-label';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import {
  useCreatePlanMutation,
  useFindPropertiesQuery,
  useUpdateSinglePlanMutation,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import { isEmpty } from 'common/utils/isEmpty';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { extractErrorMsg } from 'utils/errors';
import {
  ApplyFormContainer,
  ApplyFormField,
  ApplyFormLayout,
} from 'common/layouts/apply-form';
import { Flex, Grid, Text } from '@chakra-ui/react';
import ContactCard from 'components/shared/contact-card';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import FormError from 'common/components/form-error';
import {
  planStatusEnum,
  propertyStatusEnum,
  backgroundTypeEnum,
} from 'common/utils/enums';
import {
  createPropertyOptionMenu,
  propertyFilterOption,
} from 'common/components/dropdowns';
import SelectInput from 'common/components/select-input';
import { useNavigate } from 'react-router-dom';
import { AddressAutofill } from '@mapbox/search-js-react';
import RadioCard from 'components/shared/radio-card';
import IconImage from 'common/components/icon-image';
import { FaHouseLaptop } from 'react-icons/fa6';
import Input from 'common/components/input';

const PlanStepSelectProperty = ({
  onNextClick,
  onBackClick,
  clearSearchParams,
  planData,
}) => {
  const [wasReferred, setWasReferred] = useState(null);
  // API mutations
  const [
    createAPlan,
    { isLoading: createPlanLoading, isSuccess: isCreatePlanSuccess },
  ] = useCreatePlanMutation();
  const [
    updatePlan,
    { isLoading: updatePlanLoading, isSuccess: isUpdatePlanSuccess },
  ] = useUpdateSinglePlanMutation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEmpty(planData?.property)) {
      setWasReferred(true);
    } else if (
      !isEmpty(planData?.lease?.address?.streetAddress1) &&
      isEmpty(planData?.property)
    ) {
      setWasReferred(false);
    }
  }, [planData]);

  // User tracking
  const {
    trackFormSubmission,
    trackFormSubmissionSuccess,
    trackFormSubmissionError,
    trackCustomEvent,
    trackPageLoad,
  } = useUserTracker();

  useEffect(() => {
    trackPageLoad('PlanStepSelectProperty');
  });

  const { data: propertiesData, isLoading: findPropertiesLoading } =
    useFindPropertiesQuery({
      sort: '-createdAt',
      status: [
        propertyStatusEnum.OPTIN,
        propertyStatusEnum.OPTOUT,
        propertyStatusEnum.REQUIRED,
      ],
    });
  const propertyOptions = useMemo(
    () => createPropertyOptionMenu(propertiesData),
    [propertiesData]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={{
        property: planData?.property?.id || '',
        address: !isEmpty(planData?.lease?.address)
          ? planData.lease.address
          : planData?.property?.address || {},
      }}
      validationSchema={Yup.object().shape({
        property: Yup.string(),
        address: Yup.object().shape({
          streetAddress1: Yup.string().required('Please enter your street address'),
          streetAddress2: Yup.string(),
          country: Yup.string().required('Please select your country'),
          city: Yup.string().required('Please enter your city'),
          state: Yup.string().required('Please select your state or province'),
          postalCode: Yup.string().required(
            'Please enter your postal or zip code'
          ),
        }),
      })}
      onSubmit={(values) => {
        const submissionValues = {
          property: values?.property,
          status: planStatusEnum.PENDING,
          lease: {
            ...planData?.lease,
            address: values.address,
          },
        };

        // Handle situations where property is empty
        if (!values?.property) {
          delete submissionValues.property;
        }

        let mutation = createAPlan; // Default to createAPlan as updatePlan is commented out
        if (planData?.id) {
          submissionValues.planId = planData.id;
          mutation = updatePlan;
        }

        trackFormSubmission(submissionValues);

        mutation(submissionValues)
          .unwrap()
          .then((data) => {
            navigate(`/dashboard/plans/purchase/${data.id}`);
            onNextClick();
            trackFormSubmissionSuccess(submissionValues);
          })
          .catch((error) => {
            // Changed error message to be more generic as update is disabled
            const errMsg = extractErrorMsg(
              error,
              'There was an error while creating your plan. Please reloade the page and try again.'
            );
            toast.error(errMsg);
            trackFormSubmissionError(submissionValues, errMsg);
          });
      }}
    >
      {({
        handleBlur,
        handleChange,
        setFieldValue,
        values,
        isValid,
        dirty,
        handleSubmit,
        setFieldTouched,
        errors,
        touched,
        submitCount,
      }) => {
        const brand = planData?.property?.brand;
        const isDarkBg =
          brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;
        return (
          <Form
            onSubmit={(e) => e.preventDefault()}
            className='flex flex-col w-full h-full'
          >
            <ApplyTitle
              title='Purchase Coverage'
              subtitle='Are you renting at a ReLease-Enabled Community?'
              buttons={
                <ApplyButtons
                  loading={createPlanLoading /* || updatePlanLoading */}
                  isValid={
                    isValid &&
                    (planData?.property || dirty || planData?.lease?.address) &&
                    wasReferred !== null
                  }
                  handleSubmit={handleSubmit}
                  moveOn={!dirty}
                  onNextClick={onNextClick}
                  onBackClick={onBackClick}
                  nextTitle='Add Lease Details'
                />
              }
            />

            {/* main content */}
            <ApplyContent className='h-full justify-between'>
              {/* container */}
              <ApplyFormContainer>
                {!planData?.property && (
                  <ApplyFormLayout className=''>
                    <Grid className='grid-cols-1 md:grid-cols-2 gap-6 mb-6 col-span-2'>
                      <RadioCard
                        isSelected={wasReferred === true}
                        onClick={() => setWasReferred(true)}
                        iconComponent={
                          <IconImage
                            dark={wasReferred !== true}
                            size='3xs'
                            className='-mt-0.5'
                          />
                        }
                        label={
                          <span>Yes, I'm at a ReLease-Enabled Community</span>
                        }
                      />
                      <RadioCard
                        isSelected={wasReferred === false}
                        onClick={() => {
                          setWasReferred(false);
                          setFieldValue('property', '');
                        }}
                        icon={FaHouseLaptop}
                        label="No, I'm renting elsewhere"
                      />
                    </Grid>
                    {wasReferred !== true && (
                      <Flex className='flex-col gap-4 col-span-2 p-6 rounded-lg border-gray-200 border bg-gray-50 -mt-6 mb-6'>
                        <Flex className='flex-col gap-2 col-span-2'>
                          <Text className='font-medium text-lg inline-flex gap-3 items-center'>
                            <IconImage size='2xs' className='-mt-1' />
                            What's a ReLease-Enabled Community?
                          </Text>
                          <Text className='text-gray-500 text-base'>
                            <span className='text-black'>
                              ReLease-Enabled Communities are properties that we
                              partner with to provide you a better early move
                              out experience.
                            </span>{' '}
                            Sign a long-term lease at a ReLease Enabled
                            Community and enjoy the best rates on coverage and a
                            quicker move out from when you make a claim.
                          </Text>
                        </Flex>
                      </Flex>
                    )}
                  </ApplyFormLayout>
                )}

                {wasReferred !== null && (
                  <>
                    {wasReferred === true && (
                      <ApplyFormLayout className='pb-6'>
                        <Flex className='flex-col gap-4 col-span-2 p-6 rounded-lg border-gray-200 border bg-gray-50'>
                          <Flex className='flex-col gap-2 col-span-2'>
                            <Text className='font-medium text-lg'>
                              Which community are you renting at?
                            </Text>
                            <Text className='text-gray-500 text-base'>
                              Renters at ReLease-Enabled Communities enjoy the
                              benefits of the fastest claims and the best rates
                              on coverage.
                            </Text>
                          </Flex>
                          <ApplyFormField>
                            <WrapInputLabel>
                              Search for Community
                            </WrapInputLabel>
                            <SelectInput
                              isClearable={false}
                              options={propertyOptions}
                              isDisabled={
                                findPropertiesLoading || isEmpty(propertiesData)
                              }
                              filterOption={propertyFilterOption}
                              placeholder={
                                findPropertiesLoading
                                  ? 'Fetching...'
                                  : isEmpty(propertiesData)
                                    ? `No available communities`
                                    : 'Search by name or address'
                              }
                              name='property'
                              value={propertyOptions.find(
                                (selectedProperty) =>
                                  selectedProperty.value === values.property
                              )}
                              onChange={(selectedOption) => {
                                const propertyId = selectedOption?.value;
                                setFieldValue('property', propertyId);
                                if (selectedOption?.address) {
                                  setFieldValue(
                                    'address',
                                    selectedOption.address
                                  );
                                }
                              }}
                              onBlur={handleBlur}
                            />
                            <FormError name='property' />
                          </ApplyFormField>
                        </Flex>
                      </ApplyFormLayout>
                    )}

                    {/* Address Details */}
                    <ApplyFormLayout className='py-6'>
                      <Text className='font-medium text-lg'>
                        Rental Unit Address
                      </Text>
                      {/* Street Address 1 */}
                      <ApplyFormField>
                        <WrapInputLabel isRequired>
                          Street Address 1
                        </WrapInputLabel>
                        <AddressAutofill
                          browserAutofillEnabled={false}
                          accessToken={`${process.env.REACT_APP_MAPBOX_TOKEN}`}
                        >
                          <Input
                            autoComplete='street-address'
                            placeholderText='Start typing to search for an address'
                            value={values.address.streetAddress1}
                            onChange={({ target: { value } }) => {
                              setFieldValue('address.streetAddress1', value);
                            }}
                            onBlur={handleBlur}
                            name='address.streetAddress1'
                          />
                        </AddressAutofill>
                        <FormError name='address.streetAddress1' />
                      </ApplyFormField>

                      {/* Street Address 2 */}
                      <ApplyFormField>
                        <WrapInputLabel>Street Address 2</WrapInputLabel>
                        <Input
                          autoComplete='address-line2'
                          placeholderText='Your Address'
                          value={values.address.streetAddress2}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.streetAddress2', value);
                          }}
                          onBlur={handleBlur}
                          name='address.streetAddress2'
                        />
                        <FormError name='address.streetAddress2' />
                      </ApplyFormField>

                      {/* City */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>City</WrapInputLabel>
                        <Input
                          autoComplete='address-level2'
                          placeholderText='Your City'
                          value={values.address.city}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.city', value);
                          }}
                          onBlur={handleBlur}
                          name='address.city'
                        />
                        <FormError name='address.city' />
                      </ApplyFormField>

                      {/* State */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>State</WrapInputLabel>
                        <Input
                          autoComplete='address-level1'
                          placeholderText='Your State'
                          value={values.address.state}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.state', value);
                          }}
                          onBlur={handleBlur}
                          name='address.state'
                        />
                        <FormError name='address.state' />
                      </ApplyFormField>

                      {/* Zip Code */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>Zip Code</WrapInputLabel>
                        <Input
                          autoComplete='postal-code'
                          placeholderText='Your Postal Code'
                          value={values.address.postalCode}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.postalCode', value);
                          }}
                          onBlur={handleBlur}
                          name='address.postalCode'
                        />
                        <FormError name='address.postalCode' />
                      </ApplyFormField>

                      {/* Country */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>Country</WrapInputLabel>
                        <Input
                          autoComplete='country-name'
                          placeholderText='Your Country'
                          value={values.address.country}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.country', value);
                          }}
                          onBlur={handleBlur}
                          name='address.country'
                        />
                        <FormError name='address.country' />
                      </ApplyFormField>
                    </ApplyFormLayout>
                  </>
                )}
              </ApplyFormContainer>
              <ContactCard
                border={false}
                className='pt-4 border-t'
                title='Need help with your purchase?'
              >
                Visit our online FAQ, or contact us via email or phone to get
                help with purchasing coverage.
              </ContactCard>
            </ApplyContent>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PlanStepSelectProperty;
